import { useMutation, useQuery } from "@tanstack/react-query";
import { Alert, Button, DatePicker, Form, Input, Select, Typography } from "antd";
import type dayjs from "dayjs";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ApiError, apiFetch } from "../../api/client";
import { currentLocale } from "../../i18n";
import type { ListResponse, NotificationsResponse, PublicLanguageRow } from "../../api/types";
import { PublicLinks, PublicPage } from "./PublicPage";

const API_DATE = "YYYY-MM-DD";

interface RegisterForm {
  first_name: string;
  last_name: string;
  email: string;
  language_id: number;
  password: string;
  password_confirmation: string;
  birthday: dayjs.Dayjs;
  baptism: dayjs.Dayjs;
}

/**
 * /welcome/new_user — the registration form. The mails that invite somebody to
 * the site link straight here (SpeechplanMailer#register_new_user), which is why
 * the path is kept exactly as the old application had it.
 *
 * Nothing is created out of thin air: POST /welcome/register only succeeds for a
 * name that matches a pending invitation, or an unregistered local speaker a
 * coordinator entered. Birthday and baptism are part of that match — they build
 * the discriminator that makes a name unique — so both are required, and a
 * mismatch comes back as "no match found" rather than as a field error.
 */
export function RegisterPage() {
  const { t, i18n } = useTranslation();
  const [form] = Form.useForm<RegisterForm>();

  const languages = useQuery({
    queryKey: ["welcome", "languages"],
    queryFn: () =>
      apiFetch<ListResponse<PublicLanguageRow>>("/welcome/languages", { anonymous: true }),
  });

  // Wer das Formular ausfuellt, liest es bereits in seiner Sprache — die
  // Kopfzeile dieser Seite bietet die Umschaltung an. Dieselbe Sprache ist
  // deshalb die richtige Vorauswahl fuer das Konto, das hier entsteht.
  //
  // Die Liste kommt erst vom Server, eine Vorbelegung ueber initialValues geht
  // also nicht. Sobald der Benutzer selbst gewaehlt hat, bleibt seine Wahl
  // stehen, auch wenn er die Oberflaeche danach umschaltet.
  const chosenByHand = useRef(false);

  useEffect(() => {
    if (chosenByHand.current) return;

    const rows = languages.data?.data ?? [];
    const locale = currentLocale();
    const match = rows.find((row) => row.language_code?.toLowerCase() === locale);

    if (match) form.setFieldsValue({ language_id: match.id });
  }, [languages.data, i18n.language, form]);

  const register = useMutation({
    mutationFn: (values: RegisterForm) =>
      apiFetch<NotificationsResponse>("/welcome/register", {
        method: "POST",
        anonymous: true,
        body: {
          ...values,
          birthday: values.birthday.format(API_DATE),
          baptism: values.baptism.format(API_DATE),
        },
      }),
  });

  const failure =
    register.error instanceof ApiError
      ? register.error.message
      : register.error instanceof Error
        ? register.error.message
        : null;

  return (
    <PublicPage title={t("invitation.new_user")} width={520}>
      {register.isSuccess ? (
        <>
          <Alert type="success" showIcon title={t("user.created")} style={{ marginBottom: 16 }} />
          <Link to="/login">{t("auth.sign_in")}</Link>
        </>
      ) : (
        <>
          <Typography.Paragraph type="secondary">
            {t("invitation.new_user_account_info")}
          </Typography.Paragraph>

          {failure && (
            <Alert type="error" showIcon title={failure} style={{ marginBottom: 16 }} />
          )}

          <Form<RegisterForm>
            form={form}
            layout="vertical"
            onFinish={(values) => register.mutate(values)}
          >
            <Form.Item
              name="first_name"
              label={t("activerecord.attributes.user.first_name")}
              rules={[{ required: true }]}
            >
              <Input autoFocus />
            </Form.Item>

            <Form.Item
              name="last_name"
              label={t("activerecord.attributes.user.last_name")}
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="email"
              label={t("activerecord.attributes.user.email")}
              rules={[{ required: true, type: "email" }]}
            >
              <Input type="email" autoComplete="username" />
            </Form.Item>

            <Form.Item
              name="language_id"
              label={t("activerecord.models.language")}
              rules={[{ required: true }]}
            >
              <Select
                loading={languages.isPending}
                onChange={() => {
                  chosenByHand.current = true;
                }}
                options={(languages.data?.data ?? []).map((language) => ({
                  value: language.id,
                  label: language.denomination ?? language.language_code ?? String(language.id),
                }))}
              />
            </Form.Item>

            <Form.Item
              name="password"
              label={t("activerecord.attributes.user.password")}
              rules={[{ required: true }]}
            >
              <Input.Password autoComplete="new-password" />
            </Form.Item>

            <Form.Item
              name="password_confirmation"
              label={t("activerecord.attributes.user.password_confirmation")}
              dependencies={["password"]}
              rules={[
                { required: true },
                ({ getFieldValue }) => ({
                  validator: (_rule, value) =>
                    !value || value === getFieldValue("password")
                      ? Promise.resolve()
                      : Promise.reject(new Error(t("errors.messages.confirmation"))),
                }),
              ]}
            >
              <Input.Password autoComplete="new-password" />
            </Form.Item>

            <Form.Item
              name="birthday"
              label={t("activerecord.attributes.invitation.birthday")}
              rules={[{ required: true }]}
            >
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item
              name="baptism"
              label={t("activerecord.attributes.invitation.baptism")}
              rules={[{ required: true }]}
            >
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>

            <Button type="primary" htmlType="submit" block loading={register.isPending}>
              {t("form.send")}
            </Button>
          </Form>

          <PublicLinks exclude="/welcome/new_user" />
        </>
      )}
    </PublicPage>
  );
}
